import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const apiInstance = axios.create({ baseURL: API });

apiInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiInstance.interceptors.response.use((response) => response, async (error) => {
  const originalRequest = error.config;

  if (error.response?.status === 401 && !originalRequest._retry) {
    if (isRefreshing) {
      return new Promise(function(resolve, reject) {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers['Authorization'] = 'Bearer ' + token;
        return apiInstance(originalRequest);
      }).catch(err => {
        return Promise.reject(err);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const rt = localStorage.getItem('refresh_token');
    if (!rt) {
      return Promise.reject(error);
    }

    try {
      const { data } = await axios.post(`${API}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${rt}` }
      });
      localStorage.setItem('access_token', data.access_token);
      apiInstance.defaults.headers.common['Authorization'] = 'Bearer ' + data.access_token;
      originalRequest.headers['Authorization'] = 'Bearer ' + data.access_token;
      processQueue(null, data.access_token);
      return apiInstance(originalRequest);
    } catch (err) {
      processQueue(err, null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.reload();
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }

  return Promise.reject(error);
});

const api = {
  get: (url) => apiInstance.get(url),
  post: (url, data) => apiInstance.post(url, data),
  put: (url, data) => apiInstance.put(url, data),
  delete: (url) => apiInstance.delete(url),
};

export function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// Timetable
export const fetchCategoryTypes = () => api.get('/timetable/category-types');
export const searchCategories = (category_type_id, query) =>
  api.post('/timetable/search', { category_type_id, query });
export const fetchTimetableEvents = (category_type_id, category_id, start_date, end_date) =>
  api.post('/timetable/events', { category_type_id, category_id, start_date, end_date });

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);

// Enrollments
export const getEnrollments = () => api.get('/enrollments');
export const toggleEnrollment = (event_name, event_type) =>
  api.post('/enrollments', { event_name, event_type });
export const bulkEnroll = (event_names) =>
  api.post('/enrollments/bulk', { event_names });

// Custom Events
export const getCustomEvents = () => api.get('/events');
export const createCustomEvent = (data) => api.post('/events', data);
export const deleteCustomEvent = (id) => api.delete(`/events/${id}`);

// Auth
export const resetPassword = (data) => api.post('/auth/reset-password', data);

// Notifications
export const getVapidKey = () => api.get('/notifications/vapid-key');
export const subscribePush = (subscription) => api.post('/notifications/subscribe', subscription);
export const testNotification = () => api.post('/notifications/test');

export default api;
